"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { Geist, Geist_Mono } from "next/font/google";
import Link from "next/link";

import "./globals.css";

/* preload: false on BOTH, and it is not about this page.
 *
 * next/font emits a <link rel="preload"> for every font declared anywhere in a route's module
 * graph, and this boundary is in the root layout's graph, so its two declarations were putting
 * preloads on EVERY page of the site. That is backwards: this file renders only when the root
 * layout itself has thrown, which is approximately never, and the fonts it names are the same two
 * the layout already declares, so nothing here is ever the reason a glyph appears.
 *
 * It was also silently cancelling the layout's own choice. layout.tsx asks for Geist Mono with
 * preload: false because the first screen does not need it; these declarations resolve to the same
 * font file and asked for it to be preloaded, and preload-anywhere wins, so the mono file kept
 * racing the render-blocking stylesheets regardless. Measured on the live home page: three font
 * preloads before 2026-11-04, two after layout and Hero were changed, one after this. */
const geistSans = Geist({ variable: "--font-geist-sans", subsets: ["latin"], display: "swap", preload: false });
const geistMono = Geist_Mono({ variable: "--font-geist-mono", subsets: ["latin"], display: "swap", preload: false });

/**
 * Last-resort boundary: catches errors thrown in the root layout itself, where
 * SiteHeader/SiteFooter may not be safe to render. Replaces the whole document, so it
 * has to supply its own <html> and <body>. Deliberately plain and self-contained.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en-GB" className={`${geistSans.variable} ${geistMono.variable}`}>
      <body className="min-h-dvh bg-[#080b14] font-sans text-white antialiased">
        <main
          style={{
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem 1.5rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-0.02em" }}>
            Something went wrong
          </h1>
          <p style={{ maxWidth: "34rem", lineHeight: 1.6, color: "rgba(255,255,255,0.6)" }}>
            This page didn&apos;t load properly. Our team has been notified
            automatically. Please try again in a moment, and if it keeps happening,
            email us at team@ownlydigital.co.uk and we&apos;ll sort it.
          </p>
          <Link
            href="/"
            style={{
              marginTop: "0.75rem",
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "9999px",
              backgroundColor: "#2b7fff",
              padding: "0.75rem 1.5rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#fff",
              textDecoration: "none",
            }}
          >
            Back to home
          </Link>
        </main>
      </body>
    </html>
  );
}
